import { useRef, useState } from 'react'
import { toast } from 'sonner'
import { authApi } from '@/api/auth'
import { Button } from '@/components/ui/button'
import { Loader2, Upload, X } from 'lucide-react'

interface MultiImageUploadFieldProps {
  value: string[]
  onChange: (urls: string[]) => void
  max?: number
  placeholder?: string
}

export function MultiImageUploadField({ value, onChange, max = 9, placeholder = '上传图片' }: MultiImageUploadFieldProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return

    const valid: File[] = []
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} 超过5MB`)
        continue
      }
      if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
        toast.error(`${file.name} 不是 JPG/PNG 格式`)
        continue
      }
      valid.push(file)
    }
    if (valid.length === 0) {
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    setUploading(true)
    try {
      const urls: string[] = []
      for (const file of valid) {
        if (value.length + urls.length >= max) {
          toast.error(`最多上传 ${max} 张图片`)
          break
        }
        urls.push(await authApi.uploadImage(file))
      }
      if (urls.length > 0) onChange([...value, ...urls])
    } catch {
      toast.error('图片上传失败')
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleRemove = (url: string) => {
    onChange(value.filter(u => u !== url))
  }

  return (
    <div className="flex flex-wrap gap-2">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png"
        multiple
        className="hidden"
        onChange={handleFileChange}
      />
      {value.map(url => (
        <div key={url} className="relative inline-block">
          <img src={url} alt="preview" className="h-24 w-24 rounded-md border object-cover" />
          <Button
            type="button"
            variant="destructive"
            size="icon"
            aria-label="移除图片"
            className="absolute -right-2 -top-2 h-5 w-5"
            onClick={() => handleRemove(url)}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      ))}
      {value.length < max && (
        <Button
          type="button"
          variant="outline"
          className="h-24 w-24 flex-col gap-1"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? (
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          ) : (
            <>
              <Upload className="h-5 w-5 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">{placeholder}</span>
            </>
          )}
        </Button>
      )}
    </div>
  )
}
